import React, { useState, useRef, useContext, useEffect } from "react";
import styled from "styled-components";
import { ProfileContext } from "../contexts/ProfileContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "../components/loading-spinner";
import StackbarcartWorkhours from "../components/Stackbarcart-workhours";
import PastoralPagination from "../components/pastoral-care/components/pagination";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import OrganizationNameButton from "../components/organization-name-button";
import { OrganizationContext } from "../contexts/OrganizationContext";

const ChevronDown = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const VolunteerHours = () => {
  // ========== COMMON STATE ==========
  const [exportOption, setExportOption] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);

  // ========== PAGE-SPECIFIC STATE ==========
  const [workHoursDateRange, setWorkHoursDateRange] = useState(null);
  const [selectedBar, setSelectedBar] = useState(null);
  const [detailsData, setDetailsData] = useState([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // ========== REFS ==========
  const selectRef = useRef(null);
  const componentToPrint = useRef();

  // ========== CONTEXT ==========
  const { nameClicked } = useContext(OrganizationContext);
  const {
    showModel,
    organization,
    dateRange,
    worker,
    organizationNumber,
    getAgentWorkHours,
    getAgentWorkHoursDetails,
  } = useContext(ProfileContext);

  const queryClient = useQueryClient();
  const organizationName = worker?.organizationName;

  // ========== QUERY ==========
  const { data: workHoursData, isPending: isWorkHoursPending } = useQuery({
    queryKey: [
      "workHours",
      {
        organizationNumber,
        showModel,
        dateRange,
        workHoursDateRange,
        nameClicked,
      },
    ],
    queryFn: ({ queryKey }) => {
      const [, params] = queryKey;
      const { startDate, endDate } = getDateRange(params.workHoursDateRange);
      return getAgentWorkHours(startDate, endDate, nameClicked);
    },
    enabled: !!organizationName,
  });

  // ========== DATE HELPERS ==========
  const getDateRange = (customDateRange = null) => {
    if (customDateRange) return customDateRange;

    const today = new Date();
    let startDate, endDate;

    if (dateRange && dateRange.length === 2) {
      return {
        startDate: dateRange[0].toISOString().split("T")[0],
        endDate: dateRange[1].toISOString().split("T")[0],
      };
    }

    if (showModel === "monthly") {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = today;
    } else {
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 6);
      endDate = today;
    }

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  // ========== EFFECTS ==========
  useEffect(() => {
    if ((worker && worker.role === "subscriber") || nameClicked !== "") {
      setIsBreakdownOpen(!isBreakdownOpen);
    }
  }, []);

  useEffect(() => {
    if ((worker && worker.role === "subscriber") || nameClicked !== "") {
      setDetailsData(workHoursData?.original?.data ?? []);
    }
  }, [workHoursData]);

  useEffect(() => {
    if (organization?.id) {
      queryClient.invalidateQueries({ queryKey: ["workHours"] });
    }
  }, [organization?.id, queryClient]);

  // ========== SELECT & DATEPICKER EFFECTS ==========
  useEffect(() => {
    const selectElement = selectRef.current;
    if (!selectElement) return;

    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    const handleBlur = () => setIsSelectOpen(false);

    selectElement.addEventListener("mousedown", handleMouseDown);
    selectElement.addEventListener("blur", handleBlur);

    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  // ========== EXPORT FUNCTIONALITY ==========
  const handlePrint = useReactToPrint({
    content: () => componentToPrint.current,
    documentTitle: `Volunteer_Hours_Report_${organizationName || ""}`,
    onBeforePrint: () => {
      document.body.style.width = "100%";
    },
    onAfterPrint: () => {
      setExportOption("");
      document.body.style.width = "";
    },
  });

  const handleExportExcel = () => {
    try {
      const apiRows = workHoursData?.original?.data || [];
      const breakdownRows =
        isBreakdownOpen && detailsData?.length > 0 ? detailsData : [];

      // Determine if we should show Work Hours Summary section
      const shouldShowSummary = !(
        (worker && worker.role === "subscriber") ||
        nameClicked !== ""
      );

      // Create workbook & sheet
      const wb = XLSX.utils.book_new();

      let excelRows = [];
      let rowIndex = 0;

      // ===========================
      // 🟦 SECTION 1: Work Hours Summary (Conditional)
      // ===========================
      if (shouldShowSummary && apiRows.length > 0) {
        excelRows.push([`Work Hours Summary`]);
        rowIndex++;

        excelRows.push(["Date", "Staff_Hours", "Volunteer_Hours"]);
        rowIndex++;

        apiRows.forEach((item) => {
          excelRows.push([
            item.date,
            item.staff_hours ?? "—",
            item.volunteer_hours ?? "—",
          ]);
          rowIndex++;
        });

        // Blank row only if there will be breakdown section after
        if (breakdownRows.length > 0) {
          excelRows.push([]);
          rowIndex++;
        }
      }

      // ===========================
      // 🟩 SECTION 2: Breakdown
      // ===========================
      if (breakdownRows.length > 0) {
        const breakdownDate =
          breakdownRows?.[0]?.daily_hours?.[0]?.date ||
          breakdownRows?.[0]?.total_hours?.[0]?.date ||
          "";

        const hasVolunteer = breakdownRows.some(
          (row) => row.department === "Volunteer",
        );

        // Different title based on whether summary was shown
        if (shouldShowSummary) {
          excelRows.push([
            `Breakdown Details (${
              hasVolunteer ? "Volunteer" : "Staff"
            }) ${breakdownDate ? `(${breakdownDate})` : ""}`,
          ]);
        } else {
          // For subscriber/nameClicked view, use a simpler title
          excelRows.push([
            `Volunteer Hours Details ${breakdownDate ? `(${breakdownDate})` : ""}`,
          ]);
        }
        rowIndex++;

        // ROW TITLES (Dynamic)
        if (hasVolunteer && shouldShowSummary) {
          // Only show organization column for admin view with volunteers
          excelRows.push(["Name", "Hours", "Organization"]);
        } else if (hasVolunteer && !shouldShowSummary) {
          // For subscriber view with volunteers
          excelRows.push(["Name", "Hours", "Organization"]);
        } else if (!hasVolunteer && shouldShowSummary) {
          // Admin view without volunteers
          excelRows.push(["Name", "Hours"]);
        } else {
          // Subscriber view without volunteers (shouldn't happen but just in case)
          excelRows.push(["Name", "Hours"]);
        }
        rowIndex++;

        // ROW DATA
        breakdownRows.forEach((row) => {
          if (hasVolunteer && shouldShowSummary) {
            // Admin view with volunteers
            excelRows.push([
              row.name || "—",
              (row.daily_hours?.[0]?.hours || row.total_hours) ?? "—",
              row.organization_name || "—",
            ]);
          } else if (hasVolunteer && !shouldShowSummary) {
            // Subscriber view with volunteers
            excelRows.push([
              row.name || "—",
              row.total_hours ?? "—",
              row.organization_name || "—",
            ]);
          } else if (!hasVolunteer && shouldShowSummary) {
            // Admin view without volunteers
            excelRows.push([
              row.name || "—",
              (row.daily_hours?.[0]?.hours || row.total_hours) ?? "—",
            ]);
          } else {
            // Subscriber view without volunteers
            excelRows.push([row.name || "—", row.total_hours ?? "—"]);
          }
        });
      }

      // If no data at all
      if (excelRows.length === 0) {
        excelRows.push(["No data available"]);
      }

      // Convert rows to sheet
      const worksheet = XLSX.utils.aoa_to_sheet(excelRows);

      // ===========================
      // 🟨 Apply formatting
      // ===========================

      // Merge titles dynamically
      const merges = [];

      if (shouldShowSummary && apiRows.length > 0) {
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }); // Work Hours Summary title
      }

      if (breakdownRows.length > 0) {
        // Calculate the correct row for breakdown title
        let breakdownTitleRow = 0;
        if (shouldShowSummary && apiRows.length > 0) {
          breakdownTitleRow = apiRows.length + 2; // Title row + header row + data rows + 1 for blank row
        }

        merges.push({
          s: { r: breakdownTitleRow, c: 0 },
          e: { r: breakdownTitleRow, c: shouldShowSummary ? 2 : 1 },
        });
      }

      worksheet["!merges"] = merges;

      // Set column widths
      worksheet["!cols"] = [
        { wch: 20 }, // Name column
        { wch: 15 }, // Hours column
        ...(shouldShowSummary ||
        (breakdownRows.length > 0 &&
          breakdownRows.some((row) => row.organization_name))
          ? [{ wch: 25 }] // Organization column if needed
          : []),
      ];

      // Append to workbook
      XLSX.utils.book_append_sheet(wb, worksheet, "VolunteerHours");

      const fileName = `Volunteer_Hours_Report_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      XLSX.writeFile(wb, fileName);
      setExportOption("");
    } catch (error) {
      console.error("Excel export failed:", error);
      alert("Failed to export Excel file");
      setExportOption("");
    }
  };

  useEffect(() => {
    if (exportOption === "print") handlePrint();
    if (exportOption === "export") handleExportExcel();
  }, [exportOption]);

  // ========== PAGE-SPECIFIC HANDLERS ==========
  const handleWorkHoursFilterChange = (newDateRange) => {
    setWorkHoursDateRange(newDateRange);
  };

  const handleWorkHoursBarClick = async (type, clickedDate, modalDateRange) => {
    try {
      setSelectedBar({ type, clickedDate, modalDateRange });
      setIsDetailsLoading(true);

      // ✅ Automatically open breakdown card when bar is clicked
      if (!isBreakdownOpen) {
        setIsBreakdownOpen(true);
      }

      const { startDate, endDate } = modalDateRange;
      const res = await getAgentWorkHoursDetails(startDate, endDate, type);

      if (res?.original?.data) {
        setDetailsData(res.original.data);
      } else {
        setDetailsData([]);
      }
    } catch (error) {
      console.error("Error fetching work hours details:", error);
      setDetailsData([]);
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const toggleBreakdown = () => {
    setIsBreakdownOpen(!isBreakdownOpen);
  };

  // ========== COMPUTED VALUES ==========
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = detailsData?.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <Wrapper>
      {/* ========== STANDARDIZED TOP SECTION ========== */}
      <TopSection>
        {(worker && worker.role === "subscriber") ||
        organization?.name?.toLowerCase() === "crossroads" ? null : (
          <OrganizationNameButton />
        )}

        <TitleText>
          {(worker && worker.role === "subscriber") || nameClicked !== ""
            ? "Prayer Partner Hours"
            : "Prayer Partner Hours"}
        </TitleText>

        <SelectWrapper>
          <ExportSelect
            ref={selectRef}
            value={exportOption}
            onChange={(e) => setExportOption(e.target.value)}
          >
            <option value="" disabled>
              Export Options
            </option>
            <option value="print">Print PDF</option>
            <option value="export">Export as Excel Sheet</option>
          </ExportSelect>
          <ArrowIcon isOpen={isSelectOpen}>
            <ChevronDown />
          </ArrowIcon>
        </SelectWrapper>
      </TopSection>

      {/* ========== PAGE-SPECIFIC CONTENT ========== */}
      <printContainer ref={componentToPrint}>
        {/* ========== HIDDEN PDF-ONLY TITLE ========== */}
        <PdfOnlyHeader>
          <div className="main-title">
            {(worker && worker.role === "subscriber") || nameClicked !== ""
              ? "VOLUNTEER WORK HOURS"
              : "AGENT WORK HOURS"}
          </div>
        </PdfOnlyHeader>

        {(worker && worker.role === "subscriber") || nameClicked !== "" ? (
          <>
            {/* Volunteer-only view */}
            <BreakdownCard isOpen={isBreakdownOpen}>
              {isBreakdownOpen && (
                <BreakdownContent>
                  {isDetailsLoading ? (
                    <EmptyState>
                      <LoadingSpinner />
                    </EmptyState>
                  ) : detailsData?.length > 0 ? (
                    <>
                      <TableWrapper>
                        <TableContainer>
                          <StyledTable>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Hours</th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentItems.map((row, index) => (
                                <tr key={index}>
                                  <td>{row.name}</td>
                                  <td>{row.total_hours}</td>
                                </tr>
                              ))}
                            </tbody>
                          </StyledTable>
                        </TableContainer>
                      </TableWrapper>

                      <StyledPagination>
                        <PastoralPagination
                          total_count={detailsData.length}
                          itemsPerPage={itemsPerPage}
                          currentPage={currentPage}
                          handlePageChange={setCurrentPage}
                        />
                      </StyledPagination>
                    </>
                  ) : (
                    <EmptyState>
                      No data available of volunteer hours
                    </EmptyState>
                  )}
                </BreakdownContent>
              )}
            </BreakdownCard>
          </>
        ) : (
          <>
            {/* Admin/Agent view with chart */}
            {worker &&
              (worker.role === "super_admin" || worker.role === "admin"||worker.role === "c_suite"||worker.role === "account_manager") && (
                <StackbarcartWorkhours
                  data={workHoursData?.original?.data}
                  isLoading={isWorkHoursPending}
                  onFilterChange={handleWorkHoursFilterChange}
                  onBarClick={handleWorkHoursBarClick}
                  showModalOnClick={false}
                />
              )}

            <BreakdownCard isOpen={isBreakdownOpen}>
              <BreakdownTitle onClick={toggleBreakdown}>
                <ChevronIcon>
                  {isBreakdownOpen ? <ChevronDown /> : <ChevronRight />}
                </ChevronIcon>
                AGENT HOURS BREAKDOWN
              </BreakdownTitle>

              {isBreakdownOpen && (
                <BreakdownContent>
                  {!selectedBar ? (
                    <EmptyState>
                      Click on a bar in the chart above to view agent hour
                      details
                    </EmptyState>
                  ) : isDetailsLoading ? (
                    <EmptyState>
                      <LoadingSpinner />
                    </EmptyState>
                  ) : detailsData?.length > 0 ? (
                    <>
                      <BreakdownSubtitle>{selectedBar?.type}</BreakdownSubtitle>

                      <TableWrapper>
                        <TableContainer>
                          <StyledTable>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Name</th>
                                {selectedBar?.type?.toLowerCase() ===
                                  "volunteer" && <th>Organization</th>}
                                <th>Hours</th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentItems.map((row, index) => (
                                <tr key={index}>
                                  <td>{new Date(row.daily_hours?.[0]?.date).toLocaleDateString('en-US', {
                                        month: '2-digit',
                                        day: '2-digit',
                                        year: 'numeric',
                                    })}</td>
                                  <td>{row.name}</td>
                                  {selectedBar?.type?.toLowerCase() ===
                                    "volunteer" && (
                                    <td>{row.organization_name}</td>
                                  )}
                                  <td>{row.daily_hours?.[0]?.hours}</td>
                                </tr>
                              ))}
                            </tbody>
                          </StyledTable>
                        </TableContainer>
                      </TableWrapper>

                      <StyledPagination>
                        <PastoralPagination
                          total_count={detailsData.length}
                          itemsPerPage={itemsPerPage}
                          currentPage={currentPage}
                          handlePageChange={setCurrentPage}
                        />
                      </StyledPagination>
                    </>
                  ) : (
                    <EmptyState>
                      No data available for selected period
                    </EmptyState>
                  )}
                </BreakdownContent>
              )}
            </BreakdownCard>
          </>
        )}
      </printContainer>
    </Wrapper>
  );
};

export default VolunteerHours;

// ========== STYLED COMPONENTS ==========

// Standardized components from the example
const PdfOnlyHeader = styled.div`
  display: none;

  @media print {
    display: block;
    text-align: center;
    margin: 40px 0;
    padding-bottom: 20px;
    font-family: "Helvetica", "Arial", sans-serif;

    .main-title {
      text-align: center;
      font-family: Recoleta, serif;
      font-size: 1.25rem;
      font-weight: bold;
      color: #ec3310;
      margin-bottom: 0;
      text-decoration: underline;
      grid-column: 2;

      @media (max-width: 768px) {
        grid-column: 1;
        font-size: 1.1rem;
      }

      @media (max-width: 480px) {
        font-size: 1rem;
      }
    }
  }
`;
const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.75rem;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  justify-self: end;

  @media (max-width: 768px) {
    justify-self: center;
    width: 100%;
    max-width: 200px;
  }
`;

const ExportSelect = styled.select`
  appearance: none;
  background: #faf8f8;
  border: 1px solid #106271;
  color: #106271;
  font-family: "Space Grotesk";
  border-radius: 8px;
  width: 172px;
  height: 29px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  padding: 0 10px;

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ArrowIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  color: #6b7280;
  pointer-events: none;
  transition: transform 0.3s ease;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const TitleText = styled.h2`
  text-align: center;
  font-family: Recoleta, serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ec3310;
  margin-bottom: 0;
  text-decoration: underline;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: 1;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const printContainer = styled.div`
  width: 100%;
  display: block;
  @media print {
    width: 100%;
    display: block;
  }
`;

// Your existing styled components (keep as is)
const Wrapper = styled.div`
  width: 100%;
  padding: 2rem;
  background-color: #f3f9fa;
  min-height: 100vh;

  @media (max-width: 1024px) {
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const BreakdownCard = styled.div`
  width: 100%;
  min-height: ${({ isOpen }) => (isOpen ? "200px" : "auto")};
  margin: 2.5rem 0;
  background: #f1f1f1;
  border-radius: 16px;
  border: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  padding: ${({ isOpen }) => (isOpen ? "20px" : "16px 20px")};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: ${({ isOpen }) => (isOpen ? "16px" : "14px 16px")};
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    margin: 1.5rem 0;
    padding: ${({ isOpen }) => (isOpen ? "12px" : "10px 12px")};
    border-radius: 8px;
    min-height: ${({ isOpen }) => (isOpen ? "180px" : "auto")};
  }
`;

const BreakdownTitle = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  font-weight: 700; /* Bold */
  font-style: normal;
  line-height: 100%;
  color: #2db3c0;
  letter-spacing: 0.25em; /* 25% spacing */
  text-align: center;

  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #249aa5;
  }

  @media (max-width: 480px) {
    font-size: 14px; /* Keeping typography consistent */
    letter-spacing: 0.25em; /* Keeping consistent */
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BreakdownContent = styled.div`
  margin-top: 1rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const BreakdownSubtitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #178b96;
  text-align: center;
  margin: 0 0 1rem 0;

  &::first-letter {
    text-transform: uppercase;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    margin: 0 0 0.75rem 0;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow: hidden;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 20px;
  max-height: 600px;

  @media (max-width: 768px) {
    max-height: 500px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    max-height: 400px;
    margin-bottom: 12px;
    border-radius: 6px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  font-family: "Space Grotesk", sans-serif;
  border-collapse: collapse;
  min-width: 800px;
  background-color: #fff;

  th,
  td {
    text-align: center;
    padding: 12px;
    border-bottom: 1px solid #dee2e6;
    white-space: nowrap;
  }

  thead th {
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8f9fa;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  .text-capitalize {
    text-transform: capitalize;
  }

  @media (max-width: 1024px) {
    min-width: 700px;

    th,
    td {
      padding: 10px;
      font-size: 14px;
    }
  }

  @media (max-width: 768px) {
    min-width: 600px;

    th,
    td {
      padding: 8px;
      font-size: 13px;
    }
  }

  @media (max-width: 480px) {
    min-width: 500px;

    th,
    td {
      padding: 6px;
      font-size: 12px;
    }
  }
`;

const StyledPagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding-top: 10px;

  @media (max-width: 480px) {
    padding-top: 8px;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-family: "Space Grotesk";
  font-size: 14px;
  text-align: center;
  padding: 40px 20px;

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 30px 15px;
  }
`;
